// Метод аналізу ієрархій (АНР/AHP) — обчислення ваг критеріїв на клієнті
// для живого показу CR під час редагування матриці парних порівнянь.
// Дзеркалить формули та класичну таблицю випадкових індексів (Saaty, 1980)
// із app/decision/weights.py DSS-модуля, щоб CR у браузері збігався з тим,
// що поверне бекенд після збереження.

export const CR_THRESHOLD = 0.10;

export const RANDOM_INDEX = {
  1: 0.0,
  2: 0.0,
  3: 0.58,
  4: 0.9,
  5: 1.12,
  6: 1.24,
  7: 1.32,
  8: 1.41,
  9: 1.45,
  10: 1.49,
  11: 1.51,
  12: 1.48,
  13: 1.56,
  14: 1.57,
  15: 1.59,
};

// Шкала Сааті: значення 1/9..9 та підписи для випадаючого списку клітинки.
export const SAATY_SCALE = [
  { value: 1 / 9, label: "1/9 — абсолютно менш важливий" },
  { value: 1 / 8, label: "1/8" },
  { value: 1 / 7, label: "1/7 — значно менш важливий" },
  { value: 1 / 6, label: "1/6" },
  { value: 1 / 5, label: "1/5 — помітно менш важливий" },
  { value: 1 / 4, label: "1/4" },
  { value: 1 / 3, label: "1/3 — трохи менш важливий" },
  { value: 1 / 2, label: "1/2" },
  { value: 1, label: "1 — однакова важливість" },
  { value: 2, label: "2" },
  { value: 3, label: "3 — трохи важливіший" },
  { value: 4, label: "4" },
  { value: 5, label: "5 — помітно важливіший" },
  { value: 6, label: "6" },
  { value: 7, label: "7 — значно важливіший" },
  { value: 8, label: "8" },
  { value: 9, label: "9 — абсолютно важливіший" },
];

export function formatCellValue(value) {
  if (Math.abs(value - 1) < 1e-9) return "1";
  if (value > 1) return String(Math.round(value));
  return `1/${Math.round(1 / value)}`;
}

function multiplyMatrixVector(matrix, vector) {
  return matrix.map((row) =>
    row.reduce((sum, value, j) => sum + value * vector[j], 0)
  );
}

function principalEigenvector(matrix, iterations = 200) {
  const n = matrix.length;
  let vector = new Array(n).fill(1);
  for (let iter = 0; iter < iterations; iter += 1) {
    const next = multiplyMatrixVector(matrix, vector);
    const norm = next.reduce((sum, value) => sum + value, 0) || 1;
    vector = next.map((value) => value / norm);
  }
  return vector;
}

// Обчислює ваги критеріїв, lambda_max, CI та CR за формулами 2-5.
// matrix -- повна симетрична (за оберненими значеннями) n x n матриця.
export function computeAhpWeights(matrix) {
  const n = matrix.length;
  if (n === 0) {
    return { weights: [], lambdaMax: 0, ci: 0, cr: 0, isConsistent: true };
  }

  const weights = principalEigenvector(matrix);
  const productVector = multiplyMatrixVector(matrix, weights);
  const ratios = productVector.map((value, i) =>
    weights[i] === 0 ? 0 : value / weights[i]
  );
  const lambdaMax = ratios.reduce((sum, value) => sum + value, 0) / n;

  let ci = 0;
  let cr = 0;
  if (n >= 3) {
    ci = (lambdaMax - n) / (n - 1);
    cr = Math.abs(ci / RANDOM_INDEX[n]);
  }

  return { weights, lambdaMax, ci, cr, isConsistent: cr <= CR_THRESHOLD };
}

// Будує повну n x n матрицю з редагованих значень верхнього трикутника
// (upperValues, ключ `${i}-${j}` для i < j) -- діагональ = 1, нижній
// трикутник = обернені значення.
export function buildFullMatrix(size, upperValues) {
  const matrix = Array.from({ length: size }, () => new Array(size).fill(1));
  for (let i = 0; i < size; i += 1) {
    for (let j = i + 1; j < size; j += 1) {
      const value = upperValues[`${i}-${j}`] ?? 1;
      matrix[i][j] = value;
      matrix[j][i] = 1 / value;
    }
  }
  return matrix;
}
